import {
  ArrowRightOutlined,
  CheckOutlined,
  FileTextOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { CDS_ZINDEX, Flex } from '@revenuemarket/cds';
import { colorTokens, thematicColors } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { overlays } from '@revenuemarket/overlays';
import { DocumentArrowUp, DocumentArrowDown, Toast } from '@revenuemarket/ui';
import { Button, Drawer, DrawerProps, Tag, Typography } from 'antd';
import dayjs from 'dayjs';

import {
  IItemListItem,
  IStoreInfo,
  ModifyReason,
  TAX_INVOICE_TYPE,
  getTaxInvoice,
} from '~apis/tax-invoice';
import { useSyncInvoiceDetail } from '~hooks/queries/tax-invoice/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { EmailResendItem, TaxInvoiceType } from '~models/tax-invoice';

import HometaxInvoiceFormatModal from './HometaxInvoiceFormatModal';
import { TAX_INVOICE_REASON } from './TaxInvoiceForm/EditInfo';
import TaxInvoiceMailResendList from './TaxInvoiceMailResendList';
import TaxInvoiceResultForm, { TaxInvoiceProductForm } from './TaxInvoiceResultForm';
import { PurposeData, PurposeType, TAX_INVOICE_STATUS, TaxationData, TaxationType } from './type';
import { mapTaxInoiveRawItemToIssueInvoiceItem } from './utils';

interface Props extends DrawerProps {
  title: React.ReactNode;
  statusContent?: React.ReactNode;
  hasDetail?: boolean;
  type?: TAX_INVOICE_TYPE;
  showSync?: boolean;
  onSyncFinish?: () => void;
  id?: number;
  isForIssue?: boolean;
  modificationInfo?: {
    reason?: ModifyReason;
    type: TaxInvoiceType;
    approvalNo?: string;
  };
  status?: TAX_INVOICE_STATUS;
  representativeItem?: string;
  item: {
    totalAmount?: number;
    taxAmount?: number;
    supplyValue?: number;
    itemList: IItemListItem[];
    taxationType: TaxationType;
    businessEntityInfo: {
      businessEntityId?: number;
    };
    counterPartInfo?: {
      name?: string;
    };
    contractorInfo: Partial<IStoreInfo>;
    supplierInfo: Partial<IStoreInfo>;
    purposeType: PurposeType | null;
    issueDate: string;
    writtenDate: string;
    approvalNo?: string;
    emailResends: EmailResendItem[];
    remark?: string;
  };
}

export const ITEMS = (
  item: {
    totalAmount?: number;
    taxAmount?: number;
    supplyValue?: number;
    itemList: IItemListItem[];
    taxationType: TaxationType;
    businessEntityInfo: {
      businessEntityId?: number;
    };
    contractorInfo: Partial<IStoreInfo>;
    counterPartInfo?: {
      name?: string;
    };
    supplierInfo: Partial<IStoreInfo>;
    purposeType: PurposeType | null;
    issueDate: string;
    writtenDate: string;
    approvalNo?: string;
    remark?: string;
  },
  type?: TAX_INVOICE_TYPE,
  price?: {
    supplyPrice: number;
    taxPrice: number;
    totalPrice: number;
  },
) => {
  const counterPartName =
    (item?.counterPartInfo?.name ||
      (type === 'PURCHASE' ? item.supplierInfo.corpName : item.contractorInfo.corpName)) ??
    '-';
  return [
    {
      title: '거래처',
      value: item?.businessEntityInfo?.businessEntityId ? (
        <Button
          type="link"
          style={{ marginLeft: -10 }}
          onClick={() => {
            window.open(
              `/clobe/sales-purchase/${item.businessEntityInfo?.businessEntityId}`,
              '_blank',
            );
          }}
        >
          <Flex align="center" gap={8}>
            <Typography.Text
              style={{
                color: thematicColors.primary.medium,
              }}
            >
              {counterPartName}
            </Typography.Text>
            <ArrowRightOutlined
              style={{
                color: thematicColors.primary.medium,
              }}
              width={16}
              height={16}
            />
          </Flex>
        </Button>
      ) : (
        <Typography.Text>{counterPartName}</Typography.Text>
      ),
    },
    {
      title: '작성일자',
      value: <Typography.Text>{dayjs(item?.writtenDate).format('YYYY-MM-DD')}</Typography.Text>,
    },
    {
      title: '공급가액',
      value: (
        <Typography.Text>
          {((item?.supplyValue || price?.supplyPrice) ?? 0).toLocaleString() + '원'}
        </Typography.Text>
      ),
    },
    {
      title: '세액',
      value: (
        <Flex align="center" gap={4}>
          <Typography.Text>
            {((item?.taxAmount || price?.taxPrice) ?? 0).toLocaleString() + '원'}
          </Typography.Text>
          <Tag>{TaxationData[item.taxationType]}</Tag>
        </Flex>
      ),
    },
    {
      title: '합계금액',
      value: (
        <Flex align="center" gap={4}>
          <Typography.Text>
            {((item?.totalAmount || price?.totalPrice) ?? 0).toLocaleString() + '원'}
          </Typography.Text>{' '}
          {item.purposeType && <Tag>{PurposeData[item.purposeType]}</Tag>}
        </Flex>
      ),
    },
    ...(item.remark
      ? [
          {
            title: '비고',
            value: <Typography.Text>{item.remark}</Typography.Text>,
          },
        ]
      : []),
  ];
};

const TaxInvoiceResultDrawer: React.FC<Props> = ({
  title,
  item,
  type,
  hasDetail,
  onSyncFinish,
  showSync = true,
  isForIssue = false,
  status,
  modificationInfo,
  id,
  representativeItem,
  ...props
}) => {
  const { mutateAsync: sync, isLoading } = useSyncInvoiceDetail();

  const { companyId } = useDemoAwareCompanyId();

  const price = item.itemList.reduce(
    (acc, cur) => {
      return {
        supplyPrice: acc.supplyPrice + (cur?.supplyAmount ?? 0),
        taxPrice: acc.taxPrice + (cur?.taxAmount ?? 0),
        totalPrice: acc.totalPrice + (cur?.totalAmount ?? 0),
      };
    },
    { supplyPrice: 0, taxPrice: 0, totalPrice: 0 },
  );

  const onSyncDetail = async () => {
    if (!item.approvalNo) return;

    const data = await sync({
      companyId,
      approvalNo: item.approvalNo,
      type,
    });

    if (data.Status === ResponseStatus.SUCCESS) {
      Toast.success('상세정보 불러오기가 완료되었습니다.');
      onSyncFinish?.();
    } else {
      if (data.ErrorCode === 'RESOURCE_NOT_FOUND') {
        Toast.error('세금계산서 정보를 찾을 수 없습니다. 공동인증서를 확인해주세요.');
      } else {
        Toast.error('상세정보 불러오기가 실패했습니다.');
      }
    }
  };

  const onOpenModification = async ({
    approvalNo,
    type,
  }: {
    approvalNo: string;
    type: TaxInvoiceType;
  }) => {
    const result = await getTaxInvoice({
      companyId,
      id: approvalNo,
      type,
    });
    const record = result.Data?.data;
    if (result.Status !== ResponseStatus.SUCCESS || !record) {
      Toast.error('세금계산서 정보를 불러오는데 실패했습니다.');
    } else {
      const mappedTaxInvoice = mapTaxInoiveRawItemToIssueInvoiceItem(record);
      overlays.open(({ isOpen, onClose }) => (
        <TaxInvoiceResultDrawer
          open={isOpen}
          onClose={onClose}
          title="증빙"
          mask={false}
          width={520}
          type={type}
          id={id}
          item={{
            ...mappedTaxInvoice,
          }}
        />
      ));
    }
  };

  const onOpenHometaxFormat = () => {
    overlays.open(({ isOpen, onClose }) => (
      <HometaxInvoiceFormatModal
        open={isOpen}
        onCancel={onClose}
        supplierInfo={item.supplierInfo}
        contractorInfo={item.contractorInfo}
        itemList={item.itemList}
        approvalNo={item.approvalNo}
        writtenDate={item.writtenDate}
        supplyValue={item.supplyValue ?? price.supplyPrice}
        taxAmount={item.taxAmount ?? price.taxPrice}
        totalAmount={item.totalAmount ?? price.totalPrice}
        type={type}
        taxationType={item.taxationType}
        modificationReason={
          modificationInfo?.reason ? TAX_INVOICE_REASON[modificationInfo.reason] : undefined
        }
        purposeType={item.purposeType}
        remark={item.remark}
      />
    ));
  };

  return (
    <Drawer
      zIndex={CDS_ZINDEX.DRAWER.BASE}
      open={props.open}
      title={
        <Flex align="center" justify="space-between" gap={8}>
          {title}
          <Flex gap={8}>
            {showSync &&
              !isForIssue &&
              (hasDetail ? (
                <Button icon={<CheckOutlined />} disabled>
                  상세정보 불러오기 완료
                </Button>
              ) : (
                <Button icon={<ReloadOutlined />} onClick={onSyncDetail} loading={isLoading}>
                  상세정보 불러오기
                </Button>
              ))}
            <Button icon={<FileTextOutlined />} onClick={onOpenHometaxFormat}>
              홈택스 양식
            </Button>
          </Flex>
        </Flex>
      }
      onClose={props.onClose}
      closable={props?.closable}
      mask={props?.mask}
      {...props}
    >
      <Flex direction="column" gap={16}>
        {props?.statusContent}
        <Flex
          align="center"
          gap={8}
          style={{
            width: '100%',
          }}
        >
          <Flex gap={8} align="center">
            {type === 'SALES' ? (
              <DocumentArrowUp fill={'#1A965C'} />
            ) : (
              <DocumentArrowDown fill={'#D13030'} />
            )}
            <Typography.Title
              level={5}
              style={{
                margin: 0,
              }}
            >
              {`${type === 'SALES' ? '매출' : '매입'} 세금계산서`}
            </Typography.Title>
          </Flex>
          <Typography.Text
            style={{
              color: colorTokens.textDescription,
            }}
          >
            {item.approvalNo}
          </Typography.Text>
        </Flex>
        {modificationInfo && modificationInfo.reason && (
          <div
            style={{
              background: '#F0FFFC',
              padding: 8,
            }}
          >
            <Flex gap={8} align="center">
              <Typography.Text
                style={{
                  color: colorTokens.textDescription,
                  width: 80,
                  padding: '9px 0px 9px 8px',
                }}
              >
                수정 사유
              </Typography.Text>
              {TAX_INVOICE_REASON[modificationInfo.reason]}
            </Flex>
            {modificationInfo?.approvalNo && (
              <Flex gap={8} align="center">
                <Typography.Text
                  style={{
                    color: colorTokens.textDescription,
                    width: 80,
                    padding: '9px 0px 9px 8px',
                  }}
                >
                  수정 대상
                </Typography.Text>

                <Button
                  type="link"
                  style={{
                    marginLeft: -10,
                  }}
                  onClick={() => {
                    onOpenModification({
                      approvalNo: modificationInfo?.approvalNo ?? '',
                      type: modificationInfo.type,
                    });
                  }}
                >
                  <Flex align="center" gap={4}>
                    {modificationInfo.approvalNo}
                    <ArrowRightOutlined
                      style={{
                        color: thematicColors.primary.medium,
                      }}
                      width={16}
                      height={16}
                    />
                  </Flex>
                </Button>
              </Flex>
            )}
          </div>
        )}

        <Flex direction="column">
          {ITEMS(item, type, price).map((item) => (
            <Flex gap={8} align="center" key={item.title}>
              <Typography.Text
                style={{
                  color: colorTokens.textDescription,
                  width: 80,
                  padding: '9px 0px 9px 8px',
                }}
              >
                {item.title}
              </Typography.Text>
              {item.value}
            </Flex>
          ))}
        </Flex>

        <TaxInvoiceResultForm
          theme="red"
          title="공급자"
          showEdit={false}
          formItem={item.supplierInfo}
        />
        <TaxInvoiceResultForm
          theme="blue"
          title="공급받는자"
          showEdit={false}
          formItem={item.contractorInfo}
        />
        <TaxInvoiceProductForm
          title="품목"
          items={item.itemList}
          representativeItem={representativeItem}
        />
      </Flex>
      <Flex
        direction="column"
        gap={26}
        style={{
          marginTop: 27,
        }}
      >
        <TaxInvoiceMailResendList status={status} id={id} />
      </Flex>
    </Drawer>
  );
};

export default TaxInvoiceResultDrawer;
